import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { RmsSpinnerService } from './rms-spinner.service';
import { StorageService } from './storage.service';
import { TabService } from './tab-service';

/** One owner for local session cleanup. No logout HTTP call is required. */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly storage = inject(StorageService);
  private readonly tabs = inject(TabService);
  private readonly spinner = inject(RmsSpinnerService);
  private readonly router = inject(Router);

  private redirecting = false;

  /** Called only after a successful sign-in has stored a new identity. */
  resetRedirect(): void {
    this.redirecting = false;
  }

  /** Clean local application state without navigating (also used by the guard). */
  clearSession(): void {
    // The included spinner has a synchronous reset() implementation.
    this.spinner.reset();

    this.tabs.clearTabs();
    this.storage.removeChangePasswordToken();
    this.storage.removeItem('user');
    this.storage.removeIdentity();
  }

  /** Ignore a late 401 belonging to an older token after the user has logged in again. */
  expire(expectedToken: string | null): void {
    if (this.redirecting || this.storage.getToken() !== expectedToken) {
      return;
    }

    // Public login screens can make other requests before an identity exists.
    // Never interrupt the login form merely because an unrelated token is absent.
    if (
      expectedToken === null &&
      this.router.url.split('?')[0].split('#')[0] === '/signin'
    ) {
      return;
    }

    this.redirecting = true;
    this.clearSession();

    void this.router.navigateByUrl('/signin', { replaceUrl: true }).then(
      navigated => {
        if (!navigated && this.router.url.split('?')[0] !== '/signin') {
          this.redirecting = false;
        }
      },
      error => {
        console.error('Could not navigate to sign-in after session expiry:', error);
        this.redirecting = false;
      },
    );
  }
}
